/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnInit,
	Optional,
	Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import * as strings from '@touchplan/strings';
import { BehaviorSubject } from 'rxjs';
import { MultiWrapperComponent } from 'src/app/modules/ticket-edit/components/multi-wrapper/multi-wrapper.component';
import { TicketEditBaseComponent } from 'src/app/modules/ticket-edit/components/ticket-edit-base/ticket-edit-base.component';

interface IDropdownData {
	label: string;
	value: string; // ID, actually
	ordinal: number;
}

@Component({
	selector: 'custom-field-dropdown',
	templateUrl: './custom-field-dropdown.component.html',
	styleUrls: ['./custom-field-dropdown.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFieldDropdownComponent extends TicketEditBaseComponent implements OnInit {
	@Input() public customField: CustomFieldDefinition;
	@Input() public multiSelect = false;
	// If the field is required, the includeNotSetOption and isNotSetValid attributes will be set to false, because not set is not needed or valid in this case.
	// NOTE: if the required attritube changes its value in real time, mat-extended-select will not handle the changes to includeNotSetOption and isNotSetValid.
	@Input() public required = false;
	@Input() public placeholder: string = null;

	public internalList = [];

	public option$: BehaviorSubject<Map<string, IDropdownData>>;
	public hint: string = null;

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef
	) {
		super(control, multiWrapper, cd);
	}

	public ngOnInit() {
		super.ngOnInit();

		this.internalField.valueChanges
			.pipe(takeUntil(this.shutdownSubject))
			.subscribe((val) => this.handleSelectionChange(val as string | string[]));

		// Get the definitions of the dropdown choices:
		const dropdownChoices = this.getChoiceDefinitions();

		// The first choice is always "<not set>", but for that,
		// <mat-extended-select> has its own attribute, [includeNotSetOption]="true" ...
		//
		// Add the defined choices that are enabled:
		for (const dropdownChoice of dropdownChoices) {
			if (dropdownChoice.isEnabled) {
				this.internalList.push({
					label: dropdownChoice.label,
					value: dropdownChoice.id,
					ordinal: dropdownChoice.order,
				} as IDropdownData);
			}
		}

		const map = new Map<string, any>();
		for (const pair of this.internalList) {
			map.set(pair.label, pair);
		}
		this.option$ = new BehaviorSubject<Map<string, any>>(map);
	}

	public compareUsingCustomFieldOrdinals(a: string, b: string): number {
		const currentMap = this.option$.value;
		if (currentMap) {
			const mapValueForA = currentMap.get(a);
			const mapValueForB = currentMap.get(b);
			if (mapValueForA && mapValueForB) {
				return mapValueForA.ordinal - mapValueForB.ordinal;
			}
		}
		return 0;
	}

	/** Called when the (internal) <mat-extended-select>'s selection changes */
	private handleSelectionChange(newSelection: string | string[]) {
		this.onChange(newSelection);
		if (!this.multiSelect) {
			const newSingleSelection = newSelection as string;
			const dropdownChoice = this.getChoiceDefinitionWithId(newSingleSelection);
			if (dropdownChoice?.isEnabled) {
				// The dropdown choice is no longer hidden, clear the hint:
				this.hint = null;
			}
		}
	}

	private getChoiceDefinitions() {
		return this.customField.view.listOfValues.view.data;
	}

	private getChoiceDefinitionWithId(id: string) {
		const dropdownChoices = this.getChoiceDefinitions();
		for (const dropdownChoice of dropdownChoices) {
			if (dropdownChoice.id === id) {
				return dropdownChoice;
			}
		}
		return null;
	}

	public writeValue(value: any) {
		if (value === undefined || value === null || value === strings.UICONTROL_TEXT_NOT_SET) {
			this.internalField.setValue(null, { emitEvent: false });
			this.hint = null;
		} else {
			if (this.multiSelect) {
				// Filter out disabled dropdown choices:
				const originalChoices = value as string[];
				const filteredChoices = originalChoices.filter(
					(originalChoice) => this.getChoiceDefinitionWithId(originalChoice)?.isEnabled
				);
				this.internalField.setValue(filteredChoices, { emitEvent: true });
				this.hint = null;
			} else {
				// Single-select:
				const dropdownChoice = this.getChoiceDefinitionWithId(value);
				if (dropdownChoice?.isEnabled) {
					this.internalField.setValue(value, { emitEvent: false });
					this.hint = null;
				} else {
					// The dropdown choice has become hidden, so set the placeholder to the hidden choice's text:
					this.hint = dropdownChoice?.label;
				}
			}
		}
	}
}
